import * as layout from '../../../components/markLayout.module.css';
import React from 'react';
import { Link } from 'react-router-dom';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import BlastStatusPill from '../BlastStatusPill';
import MarkUrlList from '../../../components/MarkUrlList';
import type { BlastDetail } from '../../../types';
type Props = {
    blast: BlastDetail;
};
const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className={layout.summaryRow}>
        <P noDefaultMargin strong>
            {label}
        </P>
        <div>{children}</div>
    </div>
);
const Overview: React.FC<Props> = ({ blast }) => (
    <div className={layout.stack}>
        <div
            style={{
                marginBottom: '1.2rem'
            }}
            className={layout.panel}
        >
            <P
                strong
                noDefaultMargin
                styles={{
                    marginBottom: '0.8rem'
                }}
            >
                Structure (immutable)
            </P>
            <Row label="Status">
                <BlastStatusPill status={blast.status} />
            </Row>
            <Row label="Message class">
                <Tag
                    type={blast.message_class === 'TRANSACTIONAL' ? 'warn' : 'primary'}
                    size="small"
                >
                    {blast.message_class}
                </Tag>
            </Row>
            <Row label="Delivery scope">
                <P noDefaultMargin>
                    {blast.delivery_scope === 'PER_EMAIL' ? 'Per email' : 'Per account'}
                </P>
            </Row>
            {blast.message_class === 'TRANSACTIONAL' && (
                <Row label="Transactional approved by">
                    {blast.transactional_approved_by ? (
                        <P noDefaultMargin size="small">
                            {blast.transactional_approved_by}
                            {blast.transactional_approved_at && (
                                <>
                                    {' '}
                                    at{' '}
                                    {new Date(blast.transactional_approved_at).toLocaleString(
                                        undefined,
                                        { timeZoneName: 'short' }
                                    )}
                                </>
                            )}
                        </P>
                    ) : (
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-warning)'
                            }}
                        >
                            Auto-recorded on publish or schedule
                        </P>
                    )}
                </Row>
            )}
        </div>
        <div
            style={{
                marginBottom: '1.2rem'
            }}
            className={layout.panel}
        >
            <P
                strong
                noDefaultMargin
                styles={{
                    marginBottom: '0.8rem'
                }}
            >
                Variants
            </P>
            {blast.variants.map((v) => (
                <div
                    key={v.label}
                    style={{
                        padding: '0.4rem 0',
                        borderBottom: '1px solid var(--mark-border)'
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}
                    >
                        <P noDefaultMargin strong>
                            {v.label}
                        </P>
                        <div
                            style={{
                                display: 'flex',
                                gap: '0.8rem',
                                alignItems: 'center'
                            }}
                        >
                            <P noDefaultMargin size="small">
                                {v.ratio}%
                            </P>
                            <Tag type={v.has_content ? 'success' : 'warn'} size="small">
                                {v.has_content ? 'Content set' : 'No content'}
                            </Tag>
                        </div>
                    </div>
                    {v.subject && (
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)',
                                marginTop: '0.2rem'
                            }}
                        >
                            Subject: {v.subject}
                        </P>
                    )}
                </div>
            ))}
        </div>
        <div
            style={{
                marginBottom: '1.2rem'
            }}
            className={layout.panel}
        >
            <P
                strong
                noDefaultMargin
                styles={{
                    marginBottom: '0.8rem'
                }}
            >
                Audience & schedule
            </P>
            <Row label="Segment">
                {blast.segment_id != null ? (
                    <Link to={`/mark/segments/${blast.segment_id}`}>
                        {blast.segment_name ?? `Segment ${blast.segment_id}`}
                    </Link>
                ) : (
                    <P noDefaultMargin>(none)</P>
                )}
            </Row>
            {blast.delivery_scope === 'PER_ACCOUNT' && blast.account_recipients && (
                <Row label="Account recipients">
                    <P noDefaultMargin>{blast.account_recipients}</P>
                </Row>
            )}
            <Row label="From address">
                <P noDefaultMargin>{blast.from_address.trim() || '(default)'}</P>
            </Row>
            <Row label="Schedule">
                {/* schedule_blast sets scheduled_at but never schedule_mode, so scheduled_at wins. */}
                <P noDefaultMargin>
                    {blast.scheduled_at
                        ? new Date(blast.scheduled_at).toLocaleString(undefined, {
                              timeZoneName: 'short'
                          })
                        : blast.schedule_mode === 'scheduled' || blast.status === 'SCHEDULED'
                          ? '(not set)'
                          : 'Immediate'}
                </P>
            </Row>
            {blast.local_send_time && (
                <Row label="Local send time">
                    <P noDefaultMargin>{blast.local_send_time}</P>
                </Row>
            )}
            {blast.recipient_count != null && (
                <Row label="Recipient count">
                    <P noDefaultMargin>{blast.recipient_count.toLocaleString()}</P>
                </Row>
            )}
        </div>
        {blast.urls.length > 0 && (
            <div className={layout.panel}>
                <P
                    strong
                    noDefaultMargin
                    styles={{
                        marginBottom: '0.8rem'
                    }}
                >
                    URLs
                </P>
                <MarkUrlList urls={blast.urls} />
            </div>
        )}
    </div>
);
export default Overview;
